import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import setField from 'webapp/helpers/set-field';
import BoardLoopsDelayInputComponent from 'webapp/components/board-loops/delay-input';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectComponent from 'webapp/components/form/select';
import RowErrorComponent from 'webapp/components/row-error';
import SortableBoardsListComponent from 'webapp/components/sortable-boards-list';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';

export interface Args {
	model: BoardLoop;
	onContentChange: () => void;
	submitInProgress?: boolean;
}

export default class BoardsShareLoopBasicsComponent extends Component<Args> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	@tracked submitInProgress = false;
	@tracked title = '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.title = this.args.model.title;
	}

	get boardsList(): Board[] {
		if (this.args.model.isTemplate) {
			return this.mmBoardsService.accountTemplateBoardModels;
		} else {
			return this.mmBoardsService.normalBoardModels;
		}
	}

	get ownerIdString() {
		return this.args.model.ownerId?.toString() ?? '';
	}

	@action
	addToLoop(boardIds: number[]): void {
		this.args.model.boardIds = boardIds;

		// to recenter modal a bit later - closing modal from <SortableBoardsList>
		next(() => {
			this.args.onContentChange();
		});
	}

	@action
	removeFromLoop(boardId: number): void {
		this.args.model.boardIds = this.args.model.boardIds.filter((b) => b !== boardId);

		this.args.onContentChange();
	}

	@action
	checkToUpdateTitle(): void {
		if (this.title) {
			this.args.model.title = this.title;
		} else {
			// must not be empty
			this.title = this.args.model.title;
		}
	}

	@action
	changeOwner(value: string | null): void {
		this.args.model.ownerId = value ? +value : null;
	}
	<template>
		<p>Included dashboards ({{@model.boardIds.length}}/{{this.boardsList.length}})</p>

		<SortableBoardsListComponent
			@selected={{@model.boardIds}}
			@allItems={{this.boardsList}}
			@onAddSelected={{this.addToLoop}}
			@onRemoveSelected={{this.removeFromLoop}}
		/>

		<div class="clear"></div>

		{{#unless @model.isTemplate}}
			<div class="row parts-2">
				<div class="part">
					<FormInputComponent
						name="title"
						maxlength="50"
						disabled={{@submitInProgress}}
						@value={{this.title}}
						placeholder="Loop name"
						@focusOut={{this.checkToUpdateTitle}}
						@onEmpty={{setField target=this property="title"}}
					/>
					<RowErrorComponent @items={{@model.errors.title}} />
				</div>

				<div class="part">
					<FormSelectComponent
						name="time"
						@selected={{this.ownerIdString}}
						@options={{this.mmUsersService.activatedUsersList}}
						@placeholder="Owner"
						@showLabel2={{false}}
						@isDisabled={{@submitInProgress}}
						@isNumeric={{false}}
						@isRequired={{true}}
						@onChange={{this.changeOwner}}
					/>
				</div>
			</div>

			<BoardLoopsDelayInputComponent @model={{@model}} />
		{{/unless}}
	</template>
}
